import { useAccount } from 'wagmi';
import { AutoColumn, ColumnCenter } from '../../components/Column';
import { ConnectButton } from '../../components/ConnectButton';
import CurrencyInputPanel from '../../components/CurrencyInputPanel';
import CurrencyLogo from '../../components/CurrencyLogo';
import DoubleCurrencyLogo from '../../components/DoubleLogo';
import { AddRemoveTabs } from '../../components/NavigationTabs';
import { MinimalPositionCard } from '../../components/PositionCard';
import Row, { RowBetween, RowFixed } from '../../components/Row';
import Slider from '../../components/Slider';
import TransactionConfirmationModal from '../../components/TransactionConfirmationModal';
import { ROUTER_ADDRESS } from '../../constants';
import { useActiveWeb3React } from '../../hooks';
import { useCurrency } from '../../hooks/Tokens';
import {
  useApproveCallback,
  ApprovalState,
} from '../../hooks/useApproveCallback';
import { usePairContract } from '../../hooks/useContract';
import { Field } from '../../state/burn/actions';
import { useBurnActionHandlers } from '../../state/burn/hooks';
import { useDerivedBurnInfo, useBurnState } from '../../state/burn/hooks';
import { useTransactionAdder } from '../../state/transactions/hooks';
import {
  useUserDeadline,
  useUserSlippageTolerance,
} from '../../state/user/hooks';
import { calculateSlippageAmount, getRouterContract } from '../../utils';
//calculateGasMargin,
import { currencyId } from '../../utils/currencyId';
import useDebouncedChangeHandler from '../../utils/useDebouncedChangeHandler';
import { wrappedCurrency } from '../../utils/wrappedCurrency';
import { Wrapper } from '../Pool/styleds';
import { Currency, currencyEquals, DEV, Percent, WDEV } from '@beamswap/sdk';
import { BigNumber } from '@ethersproject/bignumber';
import { splitSignature } from '@ethersproject/bytes';
import { Contract } from '@ethersproject/contracts';
import { TransactionResponse } from '@ethersproject/providers';
import { useCallback, useMemo, useState } from 'react';
import { Link as ReactLink, RouteComponentProps } from 'react-router-dom';
import useConnectedChain from '../../hooks/useConnectedChain';
import { ConnectHandler } from '../../layout/ConnectHandler';
import {
  Button,
  TransactionDetailsBox,
  Modal,
  PixelBackground,
} from '@onbeam/ui';
import { hstack, link, text } from '@onbeam/styled-system/patterns';
import { DownIcon, PlusIcon } from '@onbeam/icons';
import { css } from '@onbeam/styled-system/css';

export default function RemoveLiquidity({
  history,
  match: {
    params: { currencyIdA, currencyIdB },
  },
}: RouteComponentProps<{ currencyIdA: string; currencyIdB: string }>) {
  const [currencyA, currencyB] = [
    useCurrency(currencyIdA) ?? undefined,
    useCurrency(currencyIdB) ?? undefined,
  ];
  const { library } = useActiveWeb3React();
  const { chainId, isSupported } = useConnectedChain();
  const { address } = useAccount();
  const [tokenA, tokenB] = useMemo(
    () => [
      wrappedCurrency(currencyA, chainId),
      wrappedCurrency(currencyB, chainId),
    ],
    [currencyA, currencyB, chainId],
  );

  // burn state
  const { independentField, typedValue } = useBurnState();
  const { pair, parsedAmounts, error } = useDerivedBurnInfo(
    currencyA ?? undefined,
    currencyB ?? undefined,
  );
  const { onUserInput: _onUserInput } = useBurnActionHandlers();
  const isValid = !error;

  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showDetailed, setShowDetailed] = useState<boolean>(false);
  const [attemptingTxn, setAttemptingTxn] = useState(false); // clicked confirm

  // txn values
  const [txHash, setTxHash] = useState<string>('');
  const [deadline] = useUserDeadline();
  const [allowedSlippage] = useUserSlippageTolerance();

  const formattedAmounts = {
    [Field.LIQUIDITY_PERCENT]: parsedAmounts[Field.LIQUIDITY_PERCENT].equalTo(
      '0',
    )
      ? '0'
      : parsedAmounts[Field.LIQUIDITY_PERCENT].lessThan(new Percent('1', '100'))
        ? '<1'
        : parsedAmounts[Field.LIQUIDITY_PERCENT].toFixed(0),
    [Field.LIQUIDITY]:
      independentField === Field.LIQUIDITY
        ? typedValue
        : parsedAmounts[Field.LIQUIDITY]?.toSignificant(6) ?? '',
    [Field.CURRENCY_A]:
      independentField === Field.CURRENCY_A
        ? typedValue
        : parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) ?? '',
    [Field.CURRENCY_B]:
      independentField === Field.CURRENCY_B
        ? typedValue
        : parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) ?? '',
  };

  // pair contract
  const pairContract: Contract | null = usePairContract(
    pair?.liquidityToken?.address,
  );

  // allowance handling
  const [signatureData, setSignatureData] = useState<{
    v: number;
    r: string;
    s: string;
    deadline: number;
  } | null>(null);
  const [approval, approveCallback] = useApproveCallback(
    parsedAmounts[Field.LIQUIDITY],
    ROUTER_ADDRESS[chainId ? chainId : ''],
  );
  async function onAttemptToApprove() {
    if (!pairContract || !pair || !library)
      throw new Error('missing dependencies');
    const liquidityAmount = parsedAmounts[Field.LIQUIDITY];
    if (!liquidityAmount) throw new Error('missing liquidity amount');
    // try to gather a signature for permission
    const nonce = await pairContract.nonces(address);

    const deadlineForSignature: number =
      Math.ceil(Date.now() / 1000) + deadline;

    const EIP712Domain = [
      { name: 'name', type: 'string' },
      { name: 'version', type: 'string' },
      { name: 'chainId', type: 'uint256' },
      { name: 'verifyingContract', type: 'address' },
    ];
    const domain = {
      name: 'Uniswap V2', //correct domain name!!!!!!!!!!!!!!!!!!!!!!!
      version: '1',
      chainId: chainId,
      verifyingContract: pair.liquidityToken.address,
    };
    const Permit = [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
      { name: 'deadline', type: 'uint256' },
    ];
    const message = {
      owner: address,
      spender: ROUTER_ADDRESS[String(chainId)],
      value: liquidityAmount.raw.toString(),
      nonce: nonce.toHexString(),
      deadline: deadlineForSignature,
    };
    const data = JSON.stringify({
      types: {
        EIP712Domain,
        Permit,
      },
      domain,
      primaryType: 'Permit',
      message,
    });

    library
      .send('eth_signTypedData_v4', [address, data])
      .then(splitSignature)
      .then((signature) => {
        setSignatureData({
          v: signature.v,
          r: signature.r,
          s: signature.s,
          deadline: deadlineForSignature,
        });
      })
      .catch((error) => {
        // for all errors other than 4001 (EIP-1193 user rejected request), fall back to manual approve
        if (error?.code !== 4001) {
          approveCallback();
        }
      });
  }

  // wrapped onUserInput to clear signatures
  const onUserInput = useCallback(
    (field: Field, typedValue: string) => {
      setSignatureData(null);
      return _onUserInput(field, typedValue);
    },
    [_onUserInput],
  );

  const onLiquidityInput = useCallback(
    (typedValue: string): void => onUserInput(Field.LIQUIDITY, typedValue),
    [onUserInput],
  );
  const onCurrencyAInput = useCallback(
    (typedValue: string): void => onUserInput(Field.CURRENCY_A, typedValue),
    [onUserInput],
  );
  const onCurrencyBInput = useCallback(
    (typedValue: string): void => onUserInput(Field.CURRENCY_B, typedValue),
    [onUserInput],
  );

  // tx sending
  const addTransaction = useTransactionAdder();
  async function onRemove() {
    if (!chainId || !library || !address)
      throw new Error('missing dependencies');
    const {
      [Field.CURRENCY_A]: currencyAmountA,
      [Field.CURRENCY_B]: currencyAmountB,
    } = parsedAmounts;
    if (!currencyAmountA || !currencyAmountB) {
      throw new Error('missing currency amounts');
    }
    const router = getRouterContract(chainId, library, address);

    const amountsMin = {
      [Field.CURRENCY_A]: calculateSlippageAmount(
        currencyAmountA,
        allowedSlippage,
      )[0],
      [Field.CURRENCY_B]: calculateSlippageAmount(
        currencyAmountB,
        allowedSlippage,
      )[0],
    };

    if (!currencyA || !currencyB) throw new Error('missing tokens');
    const liquidityAmount = parsedAmounts[Field.LIQUIDITY];
    if (!liquidityAmount) throw new Error('missing liquidity amount');

    const currencyBIsETH = currencyB === DEV;
    const oneCurrencyIsETH = currencyA === DEV || currencyBIsETH;
    const deadlineFromNow = Math.ceil(Date.now() / 1000) + deadline;

    if (!tokenA || !tokenB) throw new Error('could not wrap');

    let methodNames: string[];
    let args: Array<string | string[] | number | boolean>;
    // we have approval, use normal remove liquidity
    if (approval === ApprovalState.APPROVED) {
      // removeLiquidityETH
      if (oneCurrencyIsETH) {
        methodNames = [
          'removeLiquidityETH',
          'removeLiquidityETHSupportingFeeOnTransferTokens',
        ];
        args = [
          currencyBIsETH ? tokenA.address : tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[
            currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B
          ].toString(),
          amountsMin[
            currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A
          ].toString(),
          address,
          deadlineFromNow,
        ];
      }
      // removeLiquidity
      else {
        methodNames = ['removeLiquidity'];
        args = [
          tokenA.address,
          tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[Field.CURRENCY_A].toString(),
          amountsMin[Field.CURRENCY_B].toString(),
          address,
          deadlineFromNow,
        ];
      }
    }
    // we have a signataure, use permit versions of remove liquidity
    else if (signatureData !== null) {
      // removeLiquidityETHWithPermit
      if (oneCurrencyIsETH) {
        methodNames = [
          'removeLiquidityETHWithPermit',
          'removeLiquidityETHWithPermitSupportingFeeOnTransferTokens',
        ];
        args = [
          currencyBIsETH ? tokenA.address : tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[
            currencyBIsETH ? Field.CURRENCY_A : Field.CURRENCY_B
          ].toString(),
          amountsMin[
            currencyBIsETH ? Field.CURRENCY_B : Field.CURRENCY_A
          ].toString(),
          address,
          signatureData.deadline,
          false,
          signatureData.v,
          signatureData.r,
          signatureData.s,
        ];
      }
      // removeLiquidityETHWithPermit
      else {
        methodNames = ['removeLiquidityWithPermit'];
        args = [
          tokenA.address,
          tokenB.address,
          liquidityAmount.raw.toString(),
          amountsMin[Field.CURRENCY_A].toString(),
          amountsMin[Field.CURRENCY_B].toString(),
          address,
          signatureData.deadline,
          false,
          signatureData.v,
          signatureData.r,
          signatureData.s,
        ];
      }
    } else {
      throw new Error(
        'Attempting to confirm without approval or a signature. Please contact support.',
      );
    }
    /*
    const safeGasEstimates: (BigNumber | undefined)[] = await Promise.all(
      methodNames.map(methodName =>
        router.estimateGas[methodName](...args)
          .then(calculateGasMargin)
          .catch(error => {
            console.error(`estimateGas failed`, methodName, args, error)
            return undefined
          })
      )
    )
    */
    const safeGasEstimates: BigNumber[] = [BigNumber.from('5000000')];
    const indexOfSuccessfulEstimation = safeGasEstimates.findIndex(
      (safeGasEstimate) => BigNumber.isBigNumber(safeGasEstimate),
    );

    // all estimations failed...
    if (indexOfSuccessfulEstimation === -1) {
      console.error('This transaction would fail. Please contact support.');
    } else {
      const methodName = methodNames[indexOfSuccessfulEstimation];
      const safeGasEstimate = safeGasEstimates[indexOfSuccessfulEstimation];

      setAttemptingTxn(true);
      await router[methodName](...args, {
        gasLimit: safeGasEstimate,
      })
        .then((response: TransactionResponse) => {
          setAttemptingTxn(false);

          addTransaction(response, {
            summary: `Remove ${parsedAmounts[Field.CURRENCY_A]?.toSignificant(3)} ${currencyA?.symbol} and ${parsedAmounts[Field.CURRENCY_B]?.toSignificant(3)} ${currencyB?.symbol}`,
          });

          setTxHash(response.hash);
        })
        .catch((error: Error) => {
          setAttemptingTxn(false);
          // we only care if the error is something _other_ than the user rejected the tx
          console.error(error);
        });
    }
  }

  function modalHeader() {
    return (
      <AutoColumn $gap={'md'}>
        <RowBetween $align="flex-end">
          <p className={text({ style: 'md' })}>
            {parsedAmounts[Field.CURRENCY_A]?.toSignificant(6)}
          </p>
          <RowFixed gap="4px">
            <CurrencyLogo currency={currencyA} size={'24px'} />
            <p className={text({ style: 'md', ml: '[10px]' })}>
              {currencyA?.symbol}
            </p>
          </RowFixed>
        </RowBetween>
        <RowFixed>
          <PlusIcon />
        </RowFixed>
        <RowBetween $align="flex-end">
          <p className={text({ style: 'md' })}>
            {parsedAmounts[Field.CURRENCY_B]?.toSignificant(6)}
          </p>
          <RowFixed gap="4px">
            <CurrencyLogo currency={currencyB} size={'24px'} />
            <p className={text({ style: 'md', ml: '[10px]' })}>
              {currencyB?.symbol}
            </p>
          </RowFixed>
        </RowBetween>

        <p
          className={text({
            style: 'sm',
            color: 'mono.300',
            textAlign: 'left',
            pt: '3',
          })}
        >
          {`Output is estimated. If the price changes by more than ${
            allowedSlippage / 100
          }% your transaction will revert.`}
        </p>
      </AutoColumn>
    );
  }

  function modalBottom() {
    return (
      <>
        <div className={css({ my: '4' })}>
          <RowBetween>
            <p>{`UNI ${currencyA?.symbol}/${currencyB?.symbol}`} burned</p>
            <RowFixed>
              <DoubleCurrencyLogo
                currency0={currencyA}
                currency1={currencyB}
                margin={true}
              />
              <p>{parsedAmounts[Field.LIQUIDITY]?.toSignificant(6)}</p>
            </RowFixed>
          </RowBetween>
          {pair && (
            <>
              <RowBetween>
                <p>price</p>
                <p>
                  1 {currencyA?.symbol} ={' '}
                  {tokenA ? pair.priceOf(tokenA).toSignificant(6) : '-'}{' '}
                  {currencyB?.symbol}
                </p>
              </RowBetween>
              <RowBetween>
                <div />
                <p>
                  1 {currencyB?.symbol} ={' '}
                  {tokenB ? pair.priceOf(tokenB).toSignificant(6) : '-'}{' '}
                  {currencyA?.symbol}
                </p>
              </RowBetween>
            </>
          )}
        </div>
        <Button
          size="lg"
          disabled={
            !(approval === ApprovalState.APPROVED || signatureData !== null)
          }
          onClick={onRemove}
        >
          confirm
        </Button>
      </>
    );
  }

  const pendingText = `removing ${parsedAmounts[
    Field.CURRENCY_A
  ]?.toSignificant(6)} ${currencyA?.symbol} and ${parsedAmounts[
    Field.CURRENCY_B
  ]?.toSignificant(6)} ${currencyB?.symbol}`;

  const liquidityPercentChangeCallback = useCallback(
    (value: number) => {
      onUserInput(Field.LIQUIDITY_PERCENT, value.toString());
    },
    [onUserInput],
  );

  const oneCurrencyIsETH = currencyA === DEV || currencyB === DEV;
  const oneCurrencyIsWDEV = Boolean(
    chainId &&
      ((currencyA && currencyEquals(WDEV[chainId], currencyA)) ||
        (currencyB && currencyEquals(WDEV[chainId], currencyB))),
  );

  const handleSelectCurrencyA = useCallback(
    (currency: Currency) => {
      if (currencyIdB && currencyId(currency) === currencyIdB) {
        history.push(`/remove/${currencyId(currency)}/${currencyIdA}`);
      } else {
        history.push(`/remove/${currencyId(currency)}/${currencyIdB}`);
      }
    },
    [currencyIdA, currencyIdB, history],
  );
  const handleSelectCurrencyB = useCallback(
    (currency: Currency) => {
      if (currencyIdA && currencyId(currency) === currencyIdA) {
        history.push(`/remove/${currencyIdB}/${currencyId(currency)}`);
      } else {
        history.push(`/remove/${currencyIdA}/${currencyId(currency)}`);
      }
    },
    [currencyIdA, currencyIdB, history],
  );

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false);
    setSignatureData(null); // important that we clear signature data to avoid bad sigs
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.LIQUIDITY_PERCENT, '0');
    }
    setTxHash('');
  }, [onUserInput, txHash]);

  const [innerLiquidityPercentage, setInnerLiquidityPercentage] =
    useDebouncedChangeHandler(
      Number.parseInt(parsedAmounts[Field.LIQUIDITY_PERCENT].toFixed(0)),
      liquidityPercentChangeCallback,
    );

  return (
    <>
      <ConnectHandler>
        <AddRemoveTabs adding={false} />
        <Wrapper>
          <TransactionConfirmationModal
            isOpen={showConfirm}
            onDismiss={handleDismissConfirmation}
            attemptingTxn={attemptingTxn}
            hash={txHash ? txHash : ''}
            content={() => (
              <Modal
                open={showConfirm}
                onOpenChange={handleDismissConfirmation}
                title="you will receive"
                description=""
              >
                <TransactionDetailsBox>{modalHeader()}</TransactionDetailsBox>
                {modalBottom()}
              </Modal>
            )}
            pendingText={pendingText}
          />
          <AutoColumn $gap="md">
            <PixelBackground>
              <AutoColumn $gap="20px">
                <RowBetween>
                  <p>amount</p>
                  <button
                    type="button"
                    className={link()}
                    onClick={() => {
                      setShowDetailed(!showDetailed);
                    }}
                  >
                    {showDetailed ? 'simple' : 'detailed'}
                  </button>
                </RowBetween>
                <Row style={{ alignItems: 'flex-end' }}>
                  <p className={text({ style: 'xl' })}>
                    {formattedAmounts[Field.LIQUIDITY_PERCENT]}%
                  </p>
                </Row>
                {!showDetailed && (
                  <>
                    <Slider
                      value={innerLiquidityPercentage}
                      onChange={setInnerLiquidityPercentage}
                    />
                    <div className={hstack({ align: 'center', gap: '2' })}>
                      <Button
                        className={css({ w: 'full' })}
                        size="sm"
                        onClick={() =>
                          onUserInput(Field.LIQUIDITY_PERCENT, '25')
                        }
                      >
                        25%
                      </Button>
                      <Button
                        className={css({ w: 'full' })}
                        size="sm"
                        onClick={() =>
                          onUserInput(Field.LIQUIDITY_PERCENT, '50')
                        }
                      >
                        50%
                      </Button>
                      <Button
                        className={css({ w: 'full' })}
                        size="sm"
                        onClick={() =>
                          onUserInput(Field.LIQUIDITY_PERCENT, '75')
                        }
                      >
                        75%
                      </Button>
                      <Button
                        className={css({ w: 'full' })}
                        size="sm"
                        onClick={() =>
                          onUserInput(Field.LIQUIDITY_PERCENT, '100')
                        }
                      >
                        max
                      </Button>
                    </div>
                  </>
                )}
              </AutoColumn>
            </PixelBackground>
            {!showDetailed && (
              <>
                <ColumnCenter>
                  <DownIcon />
                </ColumnCenter>
                <PixelBackground>
                  <AutoColumn $gap="10px">
                    <RowBetween>
                      <p className={text({ style: 'md' })}>
                        {formattedAmounts[Field.CURRENCY_A] || '-'}
                      </p>
                      <RowFixed>
                        <CurrencyLogo
                          currency={currencyA}
                          style={{ marginRight: '12px' }}
                        />
                        <p
                          className={text({ style: 'md' })}
                          id="remove-liquidity-tokena-symbol"
                        >
                          {currencyA?.symbol}
                        </p>
                      </RowFixed>
                    </RowBetween>
                    <RowBetween>
                      <p className={text({ style: 'md' })}>
                        {formattedAmounts[Field.CURRENCY_B] || '-'}
                      </p>
                      <RowFixed>
                        <CurrencyLogo
                          currency={currencyB}
                          style={{ marginRight: '12px' }}
                        />
                        <p
                          className={text({ style: 'md' })}
                          id="remove-liquidity-tokenb-symbol"
                        >
                          {currencyB?.symbol}
                        </p>
                      </RowFixed>
                    </RowBetween>
                    {chainId && (oneCurrencyIsWDEV || oneCurrencyIsETH) ? (
                      <RowBetween style={{ justifyContent: 'flex-end' }}>
                        {oneCurrencyIsETH ? (
                          <ReactLink
                            className={link()}
                            to={`/remove/${
                              currencyA === DEV
                                ? WDEV[chainId].address
                                : currencyIdA
                            }/${
                              currencyB === DEV
                                ? WDEV[chainId].address
                                : currencyIdB
                            }`}
                          >
                            receive WBEAM
                          </ReactLink>
                        ) : oneCurrencyIsWDEV ? (
                          <ReactLink
                            className={link()}
                            to={`/remove/${
                              currencyA &&
                              currencyEquals(currencyA, WDEV[chainId])
                                ? 'ETH'
                                : currencyIdA
                            }/${
                              currencyB &&
                              currencyEquals(currencyB, WDEV[chainId])
                                ? 'ETH'
                                : currencyIdB
                            }`}
                          >
                            receive BEAM
                          </ReactLink>
                        ) : null}
                      </RowBetween>
                    ) : null}
                  </AutoColumn>
                </PixelBackground>
              </>
            )}

            {showDetailed && (
              <>
                <CurrencyInputPanel
                  value={formattedAmounts[Field.LIQUIDITY]}
                  onUserInput={onLiquidityInput}
                  onMax={() => {
                    onUserInput(Field.LIQUIDITY_PERCENT, '100');
                  }}
                  disableCurrencySelect
                  currency={pair?.liquidityToken}
                  pair={pair}
                  id="liquidity-amount"
                />
                <ColumnCenter>
                  <DownIcon />
                </ColumnCenter>
                <CurrencyInputPanel
                  hideBalance={true}
                  value={formattedAmounts[Field.CURRENCY_A]}
                  onUserInput={onCurrencyAInput}
                  onMax={() => onUserInput(Field.LIQUIDITY_PERCENT, '100')}
                  currency={currencyA}
                  label={'output'}
                  onCurrencySelect={handleSelectCurrencyA}
                  id="remove-liquidity-tokena"
                />
                <ColumnCenter>
                  <PlusIcon />
                </ColumnCenter>
                <CurrencyInputPanel
                  hideBalance={true}
                  value={formattedAmounts[Field.CURRENCY_B]}
                  onUserInput={onCurrencyBInput}
                  onMax={() => onUserInput(Field.LIQUIDITY_PERCENT, '100')}
                  currency={currencyB}
                  label={'output'}
                  onCurrencySelect={handleSelectCurrencyB}
                  id="remove-liquidity-tokenb"
                />
              </>
            )}
            {pair && (
              <div style={{ padding: '10px 0' }}>
                <RowBetween>
                  <p>price:</p>
                  <div>
                    1 {currencyA?.symbol} ={' '}
                    {tokenA ? pair.priceOf(tokenA).toSignificant(6) : '-'}{' '}
                    {currencyB?.symbol}
                  </div>
                </RowBetween>
                <RowBetween>
                  <div />
                  <div>
                    1 {currencyB?.symbol} ={' '}
                    {tokenB ? pair.priceOf(tokenB).toSignificant(6) : '-'}{' '}
                    {currencyA?.symbol}
                  </div>
                </RowBetween>
              </div>
            )}
            <div style={{ position: 'relative' }}>
              {!address || !isSupported ? (
                <ConnectButton />
              ) : (
                <div className={hstack({ align: 'center', gap: '2' })}>
                  <Button
                    size="lg"
                    onClick={onAttemptToApprove}
                    isLoading={approval === ApprovalState.PENDING}
                    loadingMessage="approving"
                    disabled={
                      approval !== ApprovalState.NOT_APPROVED ||
                      signatureData !== null
                    }
                  >
                    {approval === ApprovalState.APPROVED ||
                    signatureData !== null
                      ? 'approved'
                      : 'approve'}
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => {
                      setShowConfirm(true);
                    }}
                    disabled={
                      !isValid ||
                      (signatureData === null &&
                        approval !== ApprovalState.APPROVED)
                    }
                  >
                    {error || 'remove'}
                  </Button>
                </div>
              )}
            </div>
          </AutoColumn>
        </Wrapper>
      </ConnectHandler>

      {pair ? (
        <AutoColumn style={{ minWidth: '20rem', marginTop: '1rem' }}>
          <MinimalPositionCard showUnwrapped={oneCurrencyIsWDEV} pair={pair} />
        </AutoColumn>
      ) : null}
    </>
  );
}
